import { convertValueUnit } from '@metservicedev/cf-units';
import { type NcoJSONRAW, NcoVariableTree } from '@metservicedev/ncojs';
import { trimNcoJson } from '@mono/ncojson-util';
import { DateTime } from 'luxon';
import { transformTimeRangeToDurations } from 'utils/timeRangeMagic';

import {
    type DataLoadedAction,
    type ErrorLoadingDataAction,
    type LoadingDataAction,
    type SetTimeAction,
    type TemplateLoadedAction,
    TypeKeys,
} from '../../state-management/widgetblock/actions/types';
import type { Data } from '../types';
import defaultState from './defaultState';
import type { SpectraConfig, SpectraReduxState } from './types';

type Actions = LoadingDataAction | TemplateLoadedAction<SpectraConfig> | ErrorLoadingDataAction | SetTimeAction | DataLoadedAction<Data[]>;

const INITIAL_STATE: SpectraReduxState = {
    viewblockState: defaultState,
    datasets: [],
    time: {
        iso8601: new Date().toISOString(),
        timezone: 'Pacific/Auckland',
    },
};

const padNcoJson = (ncoJson: NcoJSONRAW, { start, end }: { start: string; end: string }): NcoJSONRAW => {
    const ncoJsonStartTime = DateTime.fromISO(ncoJson.variables.time.data[0]);
    const ncoJsonEndTime = DateTime.fromISO(ncoJson.variables.time.data.slice(-1)[0]);
    const reportStartTime = DateTime.fromISO(start);
    const reportEndTime = DateTime.fromISO(end);
    const shouldPadStart = ncoJsonStartTime > reportStartTime;
    const shouldPadEnd = ncoJsonEndTime < reportEndTime;

    if (!shouldPadStart && !shouldPadEnd) return ncoJson;

    const startDiff = shouldPadStart ? Math.abs(Math.floor(ncoJsonStartTime.diff(reportStartTime).as('hours'))) : 0;
    const endDiff = shouldPadEnd ? Math.abs(Math.ceil(ncoJsonEndTime.diff(reportEndTime).as('hours'))) : 0;

    const timeData = [
        ...Array(startDiff)
            .fill(undefined)
            .map((_, index) => reportStartTime.plus({ hours: index }).toUTC().toISO({ suppressMilliseconds: true })),
        ...ncoJson.variables.time.data,
        ...Array(endDiff)
            .fill(undefined)
            .map((_, index) =>
                ncoJsonEndTime
                    .plus({ hours: index + 1 })
                    .toUTC()
                    .toISO({ suppressMilliseconds: true })
            ),
    ];

    const nullEfth = Array(ncoJson.dimensions.freq)
        .fill(null)
        .map(() => Array(ncoJson.dimensions.dir).fill(null));

    const efthData = [
        ...Array(startDiff)
            .fill(undefined)
            .map(() => nullEfth),
        ...ncoJson.variables.efth.data,
        ...Array(endDiff)
            .fill(undefined)
            .map(() => nullEfth),
    ];

    return {
        ...ncoJson,
        dimensions: { ...ncoJson.dimensions, time: efthData.length },
        variables: {
            ...ncoJson.variables,
            time: { ...ncoJson.variables.time, data: timeData },
            efth: { ...ncoJson.variables.efth, data: efthData },
        },
    };
};

const undefinedIfEmpty = (value: string | undefined) => ((value?.length ?? 0) > 0 ? value : undefined);

const mergeTemplateAndData = (state: SpectraReduxState): SpectraReduxState => {
    if (!state.datasets || !state.time || !state.viewblockState || state.datasets.length === 0) {
        return {
            ...state,
            model: undefined,
            error: `${undefinedIfEmpty(state.viewblockState?.settings.title) ?? 'The Spectra'} is not yet fully configured`,
        };
    }

    const dataset = state.datasets[0];
    const unTrimmedNcoJson = dataset.cfjson;

    if (!unTrimmedNcoJson) return { ...state, model: undefined, error: 'Failed to download data' };

    const viewblockState = state.viewblockState;
    const datability = viewblockState.databilities[0];
    const timeRange = datability.timeRange;
    const time = state.time;

    const reportTime = time ? DateTime.fromISO(time.iso8601).setZone(time.timezone) : DateTime.utc();
    const { start: timeRangeStart, end: timeRangeEnd } = transformTimeRangeToDurations(timeRange!);

    const startTime = reportTime.startOf('day').plus(timeRangeStart).toUTC().toISO({ suppressMilliseconds: true });
    const endTime = reportTime.startOf('day').plus(timeRangeEnd).toUTC().toISO({ suppressMilliseconds: true });

    const trimmedNcoJson = trimNcoJson(unTrimmedNcoJson, {
        start: startTime,
        end: endTime,
    });
    const ncoJson = padNcoJson(trimmedNcoJson, {
        start: startTime,
        end: endTime,
    });

    const startDataPoint = trimmedNcoJson.variables.time.data[0];
    const startDataIndexCheck = ncoJson.variables.time.data.indexOf(startDataPoint);

    const startDataIndex = startDataIndexCheck > -1 ? startDataIndexCheck : 0;
    const endDataIndex = trimmedNcoJson.variables.time.data.length - 1 + startDataIndex;

    const ncoTree = new NcoVariableTree(ncoJson, 'efth');
    const leaves = ncoTree.flattenData();
    const xDimension = viewblockState.settings.xDimensionalVariable;
    const yDimension = viewblockState.settings.yDimensionalVariable;

    const initialState = Array.from({ length: ncoJson.dimensions[xDimension] })
        .fill(0)
        .map(() => {
            return Array.from<number>({ length: ncoJson.dimensions[yDimension] })
                .fill(0)
                .map<number[]>(() => []);
        });

    const xyData = leaves.reduce((prevState, currentNodePointer) => {
        const horizontalDimensionIndex = ncoTree.getDimensionIndex(xDimension, currentNodePointer);
        const verticalDimensionIndex = ncoTree.getDimensionIndex(yDimension, currentNodePointer);
        const data = ncoTree.getData<number>(currentNodePointer);
        const newState = prevState.slice();
        newState[horizontalDimensionIndex][verticalDimensionIndex].push(data === null ? 0 : data);
        return newState;
    }, initialState);

    const dimensionalValues: number[] = ncoJson.variables[viewblockState.settings.yDimensionalVariable === 'dir' ? 'freq' : 'dir'].data;
    const integralValues = (index: number, specDensity: number) => {
        if (index === 0 || dimensionalValues.length === index + 1) {
            return specDensity * (dimensionalValues[1] - dimensionalValues[0]);
        } else {
            return (specDensity * (dimensionalValues[index + 1] - dimensionalValues[index - 1])) / 2;
        }
    };

    const xyValues = xyData.map((yData, xIndex) => {
        if (xIndex < startDataIndex || xIndex > endDataIndex) return yData.map(() => null);

        return yData.map((data) => data.reduce((prev, specDensity, yIndex) => prev + integralValues(yIndex, specDensity), 0));
    });

    const minValue = state.viewblockState.settings.minScaleValue ?? Math.min(...xyValues.flat(3).filter((value): value is number => value !== null));
    const maxValue = Math.max(
        state.viewblockState.settings.maxScaleValue ?? Number.NEGATIVE_INFINITY,
        Math.max(...xyValues.flat(3).filter((value): value is number => value !== null))
    );

    const emptyYAxisIndexes = xyValues.reduce<boolean[]>(
        (prev, yData) => {
            const isEmpty = yData.map((value) => {
                if (value === null) return true;
                return value < minValue;
            });
            return prev.map((prevValue, index) => prevValue && isEmpty[index]);
        },
        Array.from<boolean>({ length: ncoJson.dimensions[yDimension] }).fill(true)
    );

    const indexRangeOfData: [number, number] = [emptyYAxisIndexes.indexOf(false), emptyYAxisIndexes.lastIndexOf(false)];

    const targetUnit = yDimension === 'freq' ? 's' : 'degrees';
    const xAxisData = ncoJson.variables[xDimension].data;
    const attributes = ncoJson.variables[yDimension].attributes;
    if (!attributes?.units) {
        return {
            ...state,
            model: undefined,
            error: `Unable to render Spectra as dataset "${
                dataset.dataset?.display_name_long ?? dataset.dataset?.display_name_short ?? dataset.dataset?.id ?? dataset.databilityId
            }" is missing required unit attribute. Please contact support@metoceanview.com`,
        };
    }
    const yAxisData = ncoJson.variables[yDimension].data.map((value: number) => {
        return convertValueUnit({ from: attributes.units, to: targetUnit, value });
    });
    const yAxisDataTrimmed = viewblockState.settings.trimEmptyValues ? yAxisData.slice(indexRangeOfData[0], indexRangeOfData[1] + 1) : yAxisData;
    const xyValuesTrimmed = viewblockState.settings.trimEmptyValues
        ? xyValues.map((yData) => {
              return yData.slice(indexRangeOfData[0], indexRangeOfData[1] + 1);
          })
        : xyValues;

    const yAxisUnit = yDimension === 'freq' ? 's' : '°';
    const specdenUnits = yDimension === 'freq' ? 'm².deg' : 'm².s';

    return {
        ...state,
        error: undefined,
        model: {
            minValue,
            maxValue,
            xyValues: xyValuesTrimmed,
            xAxisData,
            yAxisData: yAxisDataTrimmed,
            yAxisUnit,
            viewblockState,
            iso8601: time.iso8601,
            timezone: time.timezone,
            specdenUnits,
            datasetsWithData: state.datasets,
        },
    };
};

const reducer = (state = INITIAL_STATE, action: Actions) => {
    switch (action.type) {
        case TypeKeys.LOADING_DATA:
            return {
                ...state,
                isLoadingData: true,
            };

        case TypeKeys.ERROR_LOADING_DATA:
            return {
                ...state,
                isLoadingData: false,
            };

        case TypeKeys.SET_TIME: {
            const { iso8601, timezone } = action.payload;

            return mergeTemplateAndData({
                ...state,
                time: { iso8601, timezone },
            });
        }

        case TypeKeys.TEMPLATE_LOADED:
            return mergeTemplateAndData({
                ...state,
                viewblockState: action.payload.data,
            });

        case TypeKeys.DATA_LOADED:
            return mergeTemplateAndData({
                ...state,
                datasets: action.payload.data,
                isLoadingData: false,
            });

        default:
            return state;
    }
};

export default reducer;
